import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, throwError } from 'rxjs';
import { IAppConfig } from '../shared/interfaces/app-config';
import { StudentInfo } from '../shared/interfaces/student-info';
import { AppConfigService } from './app-config.service';
import {
  DemographicsInfo,
  EnrollmentInfo,
  HousingInfo,
  PersonalInfo,
} from '../shared/interfaces/student';
import { EventLog } from '../shared/interfaces/event-log';
import { StudentSearchSubmitNewStudentRequest } from '../modules/student-search/models/student-search-new-student-models/student-search-submit-new-student.model';
import { handleError } from '../shared/functions/error-handler';
import { StudentSearchModel } from '../modules/student-search/models/student-search-model';
import { ExistingStudents } from '../modules/student-search/models/external-search-models/existing-student-model';
import { ApiResponse } from '../shared/interfaces/api-response';
import { NewStudentModel } from '../modules/student-search/models/create-and-link-new-student-models/new-student-model';
import {
  CaseAction,
  CaseActionStatus,
  EditStudentSubmitResponse,
  LcgmsLocation,
  StudentSearchParams,
} from '../modules/student-search/models/student-search-interface';
import { StudentProfileSubmitModel } from '../shared/models/edit-student-profile-model/submit-student-profile-model';
import { AddCaseActionStatusPayload } from '../modules/referrals/interfaces/add-case-action-status-payload';
import { CreateNewStudentLinkToParentModel } from '../modules/student-search/models/create-and-link-new-student-models/create-new-student-link-to-parent-model';
import { StudentSearchNewStudentRequest } from '../modules/student-search/models/student-search-new-student-models/student-search-add-new-student.model';
import { IEPResponse } from '../modules/dashboard/interface/dashboard-response';
import { StudentDetails } from '../shared/interfaces/student-details';
import { StudentProfile } from '../shared/interfaces/student-profile';
import { EditContactSubmitModel } from '../shared/models/edit-student-profile-model/edit-contact-submit.model';
import { EditMsiGuardianResponse } from '../shared/interfaces/edit-msi-guardian-response';
import { MSIGuardianResponse } from '../shared/components/mail-to/interface/get-msi-guardian-details';
@Injectable({
  providedIn: 'root',
})
export class StudentService {
  config: IAppConfig;

  isEditingStudentInfo$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private refreshGridSubject = new BehaviorSubject<any | null>(null);

  refreshGridSubject$: Observable<boolean> =
    this.refreshGridSubject.asObservable();

  httpOptions: { headers: HttpHeaders } = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      accept: '*/*',
    }),
  };

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  refreshGridData(data): void {
    this.refreshGridSubject.next(data);
  }

  studentSearch(query: StudentSearchParams) {
    return this.http
      .post<StudentInfo[]>(`${this.config.studentApiUrl}/Student/Search`, query)
      .pipe(catchError(this.handleError));
  }

  getSearchActions(studentId: string): Observable<StudentInfo | null> {
    return this.http
      .get<StudentInfo | null>(
        `${this.config.studentApiUrl}/Student/GetSearchActions/${encodeURIComponent(studentId)}`
      )
      .pipe(catchError(this.handleError));
  }

  //todo did not find this service
  EIStudentSearch(query: any) {
    return this.http
      .post(`${this.config.studentApiUrl}/EITransition/Search`, query)
      .pipe(catchError(this.handleError));
  }

  studentDetailsById(studentOSISId: string): Observable<StudentDetails> {
    return this.http
      .get<StudentDetails>(
        `${this.config.studentApiUrl}/Student/StudentDetailsById/${studentOSISId}`
      )
      .pipe(catchError(this.handleError));
  }

  studentDetailsByIds(studentOSISId: string) {
    return this.http
      .get(
        `${this.config.studentApiUrl}/Student/StudentDetailsById/${studentOSISId}`
      )
      .pipe(catchError(this.handleError));
  }

  studentProfile(studentOSISId: string): Observable<StudentProfile> {
    return this.http
      .get<StudentProfile>(
        `${this.config.studentApiUrl}/Student/StudentProfile/${studentOSISId}`
      )
      .pipe(catchError(this.handleError));
  }

  getBasicStudentDetailsById(
    studentOSISId: string
  ): Observable<StudentDetails> {
    return this.http
      .get<StudentDetails>(
        `${this.config.studentApiUrl}/Student/BasicStudentDetailsById/${studentOSISId}`
      )
      .pipe(catchError(this.handleError));
  }

  studentIEP(studentOSISId: string) {
    return this.http
      .get<IEPResponse>(
        `${this.config.studentApiUrl}/Student/StudentIEP/${studentOSISId}`
      )
      .pipe(catchError(this.handleError));
  }

  studentEventLog(studentSk: string) {
    return this.http
      .get<EventLog[]>(
        `${this.config.studentApiUrl}/EventLog/StudentEventLogs/${studentSk}`
      )
      .pipe(catchError(this.handleError));
  }

  addEventLog(caseAction: EventLog): Observable<CaseAction> {
    return this.http
      .post<CaseAction>(
        `${this.config.apiUrl}/CaseAction/AddManualEvent/`,
        JSON.stringify(caseAction),
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }
  addCaseActionStatus(
    caseAction: AddCaseActionStatusPayload
  ): Observable<CaseActionStatus> {
    return this.http
      .post<CaseActionStatus>(
        `${this.config.apiUrl}/CaseAction/AddCaseActionStatus/`,
        caseAction,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }
  // Update the endpoint with the correct one
  addNewStudent(
    newStudent: StudentSearchSubmitNewStudentRequest
  ): Observable<ApiResponse<NewStudentModel>> {
    return this.http
      .post<ApiResponse<NewStudentModel>>(
        `${this.config.studentApiUrl}/Student/Create/`,
        newStudent,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  //searchMSI Post Call
  searchMSI(newStudent: StudentSearchModel): Observable<ExistingStudents[]> {
    return this.http
      .post<ExistingStudents[]>(
        `${this.config.studentApiUrl}/Student/SearchMSI`,
        newStudent,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  // Update the endpoint with the correct one
  addStudentandLinkWithParent(
    newStudent: CreateNewStudentLinkToParentModel
  ): Observable<StudentSearchNewStudentRequest> {
    return this.http
      .post<StudentSearchNewStudentRequest>(
        `${this.config.apiUrl}/LinkParentwithChildRelation/AddStudentandLinkWithParent`,
        newStudent,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  updateHousing(housingInfo: HousingInfo) {
    return this.http
      .post<boolean>(
        `${this.config.studentApiUrl}/Student/UpdateStudentHousingDetails/`,
        JSON.stringify(housingInfo),
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }
  updateDemographics(demographicsInfo: DemographicsInfo) {
    return this.http
      .post<boolean>(
        `${this.config.studentApiUrl}/Student/UpdateDemographicsDetails/`,
        JSON.stringify(demographicsInfo),
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }
  updateParentDetails(personalInfo: PersonalInfo) {
    return this.http
      .post<boolean>(
        `${this.config.studentApiUrl}/Student/UpdateStudentInfoDetails/`,
        JSON.stringify(personalInfo),
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }
  updateEnrollment(enrollmentInfo: EnrollmentInfo) {
    return this.http
      .post<boolean>(
        `${this.config.studentApiUrl}/Student/UpdateEnrollmentDetails/`,
        JSON.stringify(enrollmentInfo),
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  editGuardianInfo(
    payload: EditContactSubmitModel[]
  ): Observable<ApiResponse<EditMsiGuardianResponse[]>> {
    return this.http
      .post<ApiResponse<EditMsiGuardianResponse[]>>(
        `${this.config.studentApiUrl}/Student/EditMSIGuardian`,
        payload
      )
      .pipe(catchError(this.handleError));
  }

  getMsiGuardianDetails(
    studentOSISId: string
  ): Observable<MSIGuardianResponse> {
    return this.http
      .get<MSIGuardianResponse>(
        `${this.config.studentApiUrl}/Student/GetMSIGuardians/${studentOSISId}`
      )
      .pipe(catchError(this.handleError));
  }

  // TODO: Need to update this return type to what the backend returns
  editStudent(
    payload: StudentProfileSubmitModel
  ): Observable<ApiResponse<EditStudentSubmitResponse>> {
    return this.http
      .post<ApiResponse<EditStudentSubmitResponse>>(
        `${this.config.studentApiUrl}/Student/Edit`,
        payload
      )
      .pipe(catchError(this.handleError));
  }

  // Error handling
  handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }

  public getSchools(): Observable<LcgmsLocation[]> {
    return this.http
      .get<LcgmsLocation[]>(`${this.config.apiUrl}/CommonConsent/GetSchools`)
      .pipe(
        map((res: LcgmsLocation[]) =>
          !res
            ? ([{ locationCode: '', locationName: '' }] as LcgmsLocation[])
            : res
        ),
        catchError(handleError)
      );
  }
}
